// src/utils/tokenManager.ts
import parseJwt from './jwtParser';
import { getDeviceContext } from '@/pwa/pwaDetection';
import { swAuth } from './serviceWorkerAuth';
import { LOG_STYLES } from './consoleColors';
import { LAST_PUSH_TOKEN_KEY } from '@/constants/pushMessages';

export type TokenType = {
  accessToken: string;
  refreshToken?: string;
};

type GetTokenType = {
  accessToken: string | null;
  refreshToken: string | null;
};

type GetParsedTokenType = {
  accessToken: any;
  refreshToken: any;
};

export type DeviceContext = {
  isPWA: boolean;
  deviceId: string;
  platform: string;
  userAgent: string;
  screenResolution: string;
  language: string;
  timestamp: number;
  clientType: 'pwa' | 'web';
};

/**
 * TokenManager - Manages access tokens across 3 layers:
 *
 * Layer 1: Memory (this.accessTokenMemory)
 *   - Fastest access (<1ms)
 *   - Cleared on page reload
 *   - Used for every API call
 *
 * Layer 2: Service Worker (via swAuth)
 *   - Persists across page refresh (F5)
 *   - Survives hard reload (Ctrl+Shift+R)
 *   - Restores token after page reload
 *
 * Layer 3: HttpOnly Cookie (server-managed, not handled here)
 *   - Persists across browser restart
 *   - Managed by tokenRefreshService
 *   - Restores session after complete shutdown
 */
class TokenManager {
  // Layer 1: In-memory storage (fastest access)
  private accessTokenMemory: string | null = null;

  /**
   * The token of a login/register/password-reset step whose code isn't verified yet. It
   * authenticates that flow's next calls but is kept in memory only: no service worker, no
   * "authenticated" notification, no refresh. Leaving the flow or reloading drops it, so a
   * half-finished sign-in never counts as a session.
   */
  private pendingToken: string | null = null;

  setPending(accessToken: string): void {
    this.pendingToken = accessToken;
  }

  clearPending(): void {
    this.pendingToken = null;
  }

  /** The pending token while it is unexpired; API calls send it ahead of the session token. */
  getPending(): string | null {
    if (!this.pendingToken) return null;
    try {
      const { exp } = parseJwt(this.pendingToken);
      if (exp && Date.now() >= exp * 1000) this.pendingToken = null;
    } catch {
      this.pendingToken = null;
    }
    return this.pendingToken;
  }

  // Subscribers notified whenever auth state changes (set/clear), so React
  // context (AuthContext) can stay in sync instead of only reflecting
  // whatever was true at the moment it first mounted.
  private authListeners: Set<(isAuthenticated: boolean) => void> = new Set();

  subscribe(listener: (isAuthenticated: boolean) => void): () => void {
    this.authListeners.add(listener);
    return () => {
      this.authListeners.delete(listener);
    };
  }

  private notifyAuthListeners(): void {
    const isAuthenticated = this.authenticated();
    this.authListeners.forEach((listener) => listener(isAuthenticated));
  }

  private tokenExpiry: number | null = null;

  /**
   * Check if Service Worker is available and ready
   */
  // eslint-disable-next-line class-methods-use-this
  private async isServiceWorkerAvailable(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenManager] Service Worker not supported',
          LOG_STYLES.verbose
        );
      }
      return false;
    }

    try {
      await swAuth.init();
      const ready = swAuth.isReady();
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenManager] Service Worker ready:',
          LOG_STYLES.verbose,
          ready
        );
      }
      return ready;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenManager] SW init failed:',
          LOG_STYLES.verbose,
          error
        );
      }
      return false;
    }
  }

  /**
   * Clear all tokens and device context
   * Called on logout
   */
  clear(): void {
    if (process.env.NODE_ENV !== 'production') {
      console.log('%c[TokenManager] Clearing tokens...', LOG_STYLES.info);
    }

    // Clear memory
    this.accessTokenMemory = null;
    this.tokenExpiry = null;
    this.pendingToken = null;

    // Clear device context (prevents auto-restore on next visit)
    localStorage.removeItem('device_context');
    localStorage.removeItem(LAST_PUSH_TOKEN_KEY); // ← force push re-registration on next login (fixes account-switch)

    // Clear Service Worker token (best effort - may fail if SW unavailable)
    swAuth.clearAccessToken().catch((err) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenManager] SW clear failed (expected if unavailable):',
          LOG_STYLES.verbose,
          err
        );
      }
    });
    if (process.env.NODE_ENV !== 'production') {
      console.log('%c[TokenManager] ✅ Tokens cleared', LOG_STYLES.success);
    }
    this.notifyAuthListeners();
  }

  /**
   * Async wrapper for clear() - for backward compatibility
   */
  async asyncClear() {
    return new Promise((resolve, reject) => {
      try {
        this.clear();
        resolve(1);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Store access token in all 3 layers:
   * 1. Decode and validate token
   * 2. Store in Service Worker (if available)
   * 3. Store in memory (always)
   * 4. Update device_context in localStorage
   *
   * Called after: Login, MFA verification, token refresh, security settings changes
   */
  async set(token: TokenType) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('%c[TokenManager] Setting token...', LOG_STYLES.info);
    }
    // A real session ends whatever flow was pending.
    this.pendingToken = null;

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const { accessToken } = token;

        // Validate token exists
        if (!accessToken) {
          if (process.env.NODE_ENV !== 'production') {
            console.error(
              '%c[TokenManager] ❌ No access token provided!',
              LOG_STYLES.error
            );
          }
          reject(new Error('No access token'));
          return;
        }

        // Decode token to get expiry time
        try {
          const decoded: any = parseJwt(accessToken);
          this.tokenExpiry = decoded.exp * 1000; // Convert to milliseconds
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              '%c[TokenManager] Token expires:',
              LOG_STYLES.verbose,
              new Date(this.tokenExpiry).toLocaleString()
            );
          }
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              '%c[TokenManager] Failed to decode token:',
              LOG_STYLES.verbose,
              e
            );
          }
        }

        // Try to store in Service Worker (Layer 2)
        const swAvailable = await this.isServiceWorkerAvailable();

        if (swAvailable) {
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              '%c[TokenManager] Storing in Service Worker...',
              LOG_STYLES.info
            );
          }
          const success = await swAuth.setAccessToken(accessToken);

          if (success) {
            if (process.env.NODE_ENV !== 'production') {
              console.log(
                '%c[TokenManager] ✅ Token stored in SW',
                LOG_STYLES.success
              );
            }
          } else if (process.env.NODE_ENV !== 'production') {
            console.log(
              '%c[TokenManager] SW storage failed, using memory only',
              LOG_STYLES.verbose
            );
          }
        } else if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenManager] SW not available, using memory only',
            LOG_STYLES.verbose
          );
        }

        // Always store in memory (Layer 1) - fastest access for API calls
        this.accessTokenMemory = accessToken;

        // Update device context (used to detect previous sessions)
        const deviceContext = getDeviceContext();
        localStorage.setItem('device_context', JSON.stringify(deviceContext));
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenManager] Device context updated',
            LOG_STYLES.verbose
          );
        }

        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenManager] ✅ Token set successfully',
            LOG_STYLES.success
          );
        }
        this.notifyAuthListeners();
        resolve(token);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(
            '%c[TokenManager] ❌ Error setting token:',
            LOG_STYLES.error,
            error
          );
        }
        reject(error);
      }
    });
  }

  /**
   * Get tokens synchronously (only from memory)
   * Use getAccessToken() for async access with SW fallback
   */
  get(): GetTokenType {
    return {
      accessToken: this.accessTokenMemory,
      refreshToken: null
    };
  }

  /**
   * Get access token asynchronously
   * Checks in order:
   * 1. Memory (fast)
   * 2. Service Worker (slower, but persists across refresh)
   * 3. Returns null if not found
   *
   * Used by: API calls, token refresh service, auth checks
   */
  async getAccessToken(): Promise<string | null> {
    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '%c[TokenManager] Getting access token...',
        LOG_STYLES.verbose
      );
    }

    // Check memory first (fastest)
    if (this.accessTokenMemory) {
      // Validate token hasn't expired
      if (this.tokenExpiry && Date.now() >= this.tokenExpiry) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenManager] Memory token expired',
            LOG_STYLES.verbose
          );
        }
        this.accessTokenMemory = null;
        return null;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenManager] Returning token from memory',
          LOG_STYLES.verbose
        );
      }
      return this.accessTokenMemory;
    }

    // Memory empty - try Service Worker
    const swAvailable = await this.isServiceWorkerAvailable();
    if (swAvailable) {
      // if (swAuth.isReady()) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenManager] Checking Service Worker...',
          LOG_STYLES.verbose
        );
      }
      const token = await swAuth.getAccessToken();

      if (token) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenManager] ✅ Got token from Service Worker',
            LOG_STYLES.info
          );
        }

        // Cache in memory for faster subsequent access
        this.accessTokenMemory = token;

        // Decode and store expiry
        try {
          const decoded: any = parseJwt(token);
          this.tokenExpiry = decoded.exp * 1000;
        } catch (e) {
          // Ignore decode errors
        }

        return token;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log('%c[TokenManager] No token in SW', LOG_STYLES.verbose);
      }
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log('%c[TokenManager] No token available', LOG_STYLES.verbose);
    }
    return null;
  }

  /**
   * Get parsed tokens (decoded JWT)
   * Returns decoded token payload or empty object
   */
  parsed(): GetParsedTokenType {
    const token = this.get();
    return {
      accessToken: token.accessToken ? parseJwt(token.accessToken) : {},
      refreshToken: {}
    };
  }

  /**
   * ASYNC: Check if we have a valid token
   * Checks memory + Service Worker
   *
   * Use this for authentication logic (PrivateRoute, etc.)
   * Returns true only if token exists AND hasn't expired
   */
  async hasToken(): Promise<boolean> {
    // Check memory first
    if (this.accessTokenMemory) {
      // Validate expiry
      if (this.tokenExpiry && Date.now() >= this.tokenExpiry) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenManager] hasToken: false (expired)',
            LOG_STYLES.verbose
          );
        }
        return false;
      }
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenManager] hasToken: true (from memory)',
          LOG_STYLES.verbose
        );
      }
      return true;
    }

    // Check Service Worker
    const swAvailable = await this.isServiceWorkerAvailable();
    if (swAvailable) {
      // if (swAuth.isReady()) {
      const token = await swAuth.getAccessToken();
      if (token) {
        // Found in SW - cache in memory
        this.accessTokenMemory = token;

        // Decode and check expiry
        try {
          const decoded: any = parseJwt(token);
          this.tokenExpiry = decoded.exp * 1000;

          if (Date.now() >= this.tokenExpiry) {
            if (process.env.NODE_ENV !== 'production') {
              console.log(
                '%c[TokenManager] hasToken: false (SW token expired)',
                LOG_STYLES.verbose
              );
            }
            return false;
          }
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              '%c[TokenManager] Failed to decode SW token:',
              LOG_STYLES.verbose,
              e
            );
          }
        }

        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenManager] hasToken: true (from SW, cached to memory)',
            LOG_STYLES.info
          );
        }
        return true;
      }
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '%c[TokenManager] hasToken: false (no token anywhere)',
        LOG_STYLES.verbose
      );
    }
    return false;
  }

  /**
   * SYNC: Quick check - only checks memory
   * Use ONLY for UI rendering (show/hide elements)
   * For authentication logic, use hasToken() instead!
   *
   * @deprecated Use hasToken() for authentication checks
   */
  authenticated(): boolean {
    if (this.accessTokenMemory) {
      // Check expiry
      if (this.tokenExpiry && Date.now() >= this.tokenExpiry) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(
            '%c[TokenManager] authenticated: false (expired)',
            LOG_STYLES.verbose
          );
        }
        return false;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[TokenManager] authenticated: true (memory has valid token)',
          LOG_STYLES.verbose
        );
      }
      return true;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '%c[TokenManager] authenticated: false (no token in memory)',
        LOG_STYLES.verbose
      );
    }
    return false;
  }

  /**
   * Check if user had a previous session on this device
   * Used to determine if we should attempt HttpOnly cookie restore
   *
   * Returns true if device_context exists in localStorage
   * This indicates the user was logged in before on this device
   */
  // eslint-disable-next-line class-methods-use-this
  hadPreviousSession(): boolean {
    const hasDeviceContext = !!localStorage.getItem('device_context');
    if (process.env.NODE_ENV !== 'production') {
      console.log(
        '%c[TokenManager] hadPreviousSession:',
        LOG_STYLES.verbose,
        hasDeviceContext
      );
    }
    return hasDeviceContext;
  }

  /**
   * Get stored device context
   */
  // eslint-disable-next-line class-methods-use-this
  getDeviceContext(): DeviceContext | null {
    const stored = localStorage.getItem('device_context');
    if (!stored) return null;

    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
}

export default new TokenManager();
